import { harvestWidgets, warmWidgetCache } from './reportBuild';
import { useCallback, useState } from 'react';
import { nameReport } from '@/utils/api/reports.api';
import { trackReportCreated } from './reportAnalytics';
import { useCreateReport } from '@/utils/hooks/reports';
import { useQueryClient } from 'react-query';

/**
 * Turn a finished assistant chat into a saved report: harvest its renderable
 * get_entity_data calls into widget configs, warm the widget cache from the
 * chat's tool outputs (so the report view renders without a loader flash), name
 * the report (from the caller-provided title, falling back to naming it from
 * the prompt), then create it. Resolves to the created report, or null when the
 * chat has nothing renderable to harvest (the caller decides the UX).
 *
 * The create path for every chat-derived report, called only by the assistant's
 * create_report tool executor (useCreateReportTool). Colocated with the Reports
 * views.
 */
export default function useCreateReportFromChat() {
    const queryClient = useQueryClient();
    const { mutateAsync: createReport } = useCreateReport();
    const [isSaving, setIsSaving] = useState(false);

    const createFromChat = useCallback(
        async ({ prompt, messages, title = '', source }) => {
            const widgets = harvestWidgets(messages);
            if (!widgets.length) return null;
            setIsSaving(true);
            try {
                warmWidgetCache(queryClient, widgets, messages);
                let reportTitle = title.trim();
                if (!reportTitle) {
                    ({ title: reportTitle } = await nameReport({
                        prompt,
                    }).catch(() => ({ title: '' })));
                }
                const report = await createReport({
                    prompt,
                    title: reportTitle,
                    widgets,
                });
                trackReportCreated({
                    source,
                    reportId: report.id,
                    widgets,
                });
                return report;
            } finally {
                setIsSaving(false);
            }
        },
        [queryClient, createReport],
    );

    return { createFromChat, isSaving };
}
